import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import { EditOutlined } from "@mui/icons-material";
import { useForm } from "react-hook-form";
import CustomCheckbox from "../../common/CustomCheckBox";
import SignaturePad, { SignatureData } from "../../common/signature/SignaturePad";
import { ProviderData } from "../ivr/IVRForm";
import { useModalStyles } from "../../common/useModalStyles";
import { FilePenLine } from "lucide-react";

interface ProductItemProps {
  open: boolean;
  onClose: () => void;
  addProductItem: (productItem: any) => void;
  productItemDetails: any;
  isEdit: boolean;
  practiceName?: string;
  selectedProviderData?: ProviderData[];
  addSignature: (signature: any) => void;
  from?: string;
  authorizedSigner?: boolean;
  authorizedSignerName?: any;
}

const SigantureSubmitPopup: React.FC<ProductItemProps> = ({
  open,
  onClose,
  addProductItem,
  productItemDetails,
  isEdit,
  practiceName,
  selectedProviderData,
  addSignature,
  from,
  authorizedSigner = true,
  authorizedSignerName,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens: modalTokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    primaryBtnSx,
    cancelBtnSx,
  } = useModalStyles();

  const { control, handleSubmit, reset, watch, setValue } = useForm<any>();

  const [signOutSideName, setSignOutSideName] = useState("");
  const [signatureData, setSignatureData] = useState("");
  const [drawName, setDrawName] = useState("");

  useEffect(() => {
    if (open) {
      reset(productItemDetails);
    }
    if (!open) {
      setDefaultValues();
    }
  }, [open]);

  const setDefaultValues = useCallback(() => {
    reset();
  }, []);

  const signaturePadValidations = {
    name: {
      required: "Name is required.",
    },
    signature: {
      required: "Signature is required.",
    },
  };

  useEffect(() => {
    if (authorizedSigner === false && selectedProviderData && selectedProviderData.length > 0) {
      setSignOutSideName(selectedProviderData[0].providerName);
    } else {
      setSignOutSideName("");
    }
  }, [selectedProviderData, authorizedSigner]);

  const onSubmit = useCallback(
    (data: any) => {
      addSignature(data);
      onClose();
    },
    [addSignature, onClose]
  );

  const handleSignatureChange = (data: SignatureData) => {
    setSignatureData(data.signature);
    setValue("providerInformation.signature", data.signature);
    setValue("providerInformation.name", data.typedText);
    if (data.signature && data.typedText) {
      setValue("checkProviderSignAuth", true);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const titleFrom = from ? from : "Order";

  return (
    <Dialog
      open={open}
      keepMounted
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      onClick={(event) => {
        event.stopPropagation();
      }}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(modalTokens.accent)}>
          <FilePenLine size={22} color={modalTokens.accent} strokeWidth={1.8} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            fontWeight={700}
            sx={{
              fontSize: "1.1rem",
              color: modalTokens.textPrimary,
              lineHeight: 1.3,
            }}
          >
            Submit {titleFrom} Confirmation
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: modalTokens.textSecondary, mt: 0.15 }}>
            Sign to confirm submission
          </Typography>
        </Box>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        <Box sx={{ ...bodySx, minHeight: 0 }}>
          <Grid container spacing={2}>
            {practiceName ? (
              <Grid item xs={12}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderRadius: "10px",
                    backgroundColor: modalTokens.cardBg,
                    border: `1px solid ${modalTokens.border}`,
                  }}
                >
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.88rem", fontWeight: 700, color: modalTokens.textSecondary }}
                  >
                    Practice:{" "}
                  </Typography>
                  <Typography
                    component="span"
                    sx={{ fontSize: "0.88rem", fontWeight: 700, color: modalTokens.accent }}
                  >
                    {practiceName || "N/A"}
                  </Typography>
                </Box>
              </Grid>
            ) : null}
            {authorizedSigner && (
              <Grid item md={12} xs={12}>
                <CustomCheckbox
                  sx={{ pl: 0 }}
                  control={control}
                  controllerName="checkProviderSignAuth"
                  label={`I am authorized to sign this IVR form on behalf of the ${
                    selectedProviderData ? selectedProviderData[0]?.providerName ?? "<Provider Name>" : ""
                  }`}
                  rules={{
                    required:
                      "Please confirm that you are authorized to sign this IVR form on behalf of the provider.",
                  }}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    if (checked === true) {
                      if (signatureData && selectedProviderData && selectedProviderData.length > 0) {
                        const providerName = selectedProviderData[0].providerName;
                        setDrawName(providerName);
                        setValue("providerInformation.name", providerName, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      } else if (selectedProviderData && selectedProviderData.length > 0) {
                        setSignOutSideName(selectedProviderData[0].providerName);
                        setSignatureData("");
                      }
                    } else {
                      setSignOutSideName("");
                      setDrawName("");
                    }
                  }}
                />
              </Grid>
            )}
            <Grid
              item
              md={12}
              xs={12}
              sx={{
                ...(isMobile && {
                  width: "100%",
                  maxWidth: "100%",
                  flex: 1,
                }),
                ...(!isMobile && {
                  minWidth: "min(100%, 550px)",
                }),
              }}
            >
              <SignaturePad
                onSignatureChange={handleSignatureChange}
                name="providerInformation.signature"
                textName="providerInformation.name"
                control={control}
                validationRules={signaturePadValidations}
                setSignOutside={signOutSideName || authorizedSignerName}
                disabled={false}
                signature=""
                signerName={drawName}
              />
            </Grid>
          </Grid>
        </Box>

        <Box sx={footerSx}>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", flexWrap: "wrap" }}>
            <Button
              type="submit"
              variant="contained"
              startIcon={<EditOutlined sx={{ fontSize: "1rem !important" }} />}
              sx={primaryBtnSx}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              startIcon={<CancelIcon sx={{ fontSize: "1rem !important" }} />}
              sx={cancelBtnSx}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default SigantureSubmitPopup;
